import { useEffect, useId, useRef, useState } from 'react';
import { Icono } from './Icono';
import type { NombreIcono } from './iconos';
import { TEXTOS_PIEZAS, type IdiomaPieza } from './textosPiezas';
import './piezas.css';

/**
 * RV Menu (componentes.md §12c): el menú de acciones «⋯» de una ficha, una fila o una tarjeta. Sale del botón anclado a
 * él; en móvil, hoja desde abajo con «Cerrar». Una opción con `submenu` lo abre dentro del mismo menú con «‹ Atrás». Esc,
 * tocar fuera o elegir lo cierran y devuelven el foco al botón. Lo peligroso (`peligro`) va en rojo y al final. `idioma`
 * (G928, G996) pone en inglés «More options», «Back», «Close», «Close the menu» y «Submenu».
 */
export interface OpcionMenu {
    clave: string;
    texto: string;
    icono?: NombreIcono;
    /** Una línea gris debajo del texto. */
    detalle?: string;
    peligro?: boolean;
    desactivada?: boolean;
    onClick?: () => void;
    submenu?: readonly OpcionMenu[];
}

interface Props {
    opciones: readonly OpcionMenu[];
    /** La aria del botón «⋯»; por defecto «Más opciones» (o «More options»). */
    etiqueta?: string;
    movil?: boolean;
    /** Botón fantasma (sin fondo), como en «Lo próximo» de las páginas del cliente. */
    fantasma?: boolean;
    idioma?: IdiomaPieza;
}

export function Menu({ opciones, etiqueta, movil = false, fantasma = false, idioma = 'es' }: Props) {
    const t = TEXTOS_PIEZAS[idioma];
    const [abierto, setAbierto] = useState(false);
    const [sub, setSub] = useState<OpcionMenu | null>(null);
    const caja = useRef<HTMLDivElement>(null);
    const boton = useRef<HTMLButtonElement>(null);
    const id = useId();

    const cerrar = (foco = true) => {
        setAbierto(false);
        setSub(null);
        if (foco) {
            boton.current?.focus();
        }
    };

    useEffect(() => {
        if (!abierto) {
            return;
        }
        const fuera = (e: PointerEvent | MouseEvent) => {
            if (caja.current !== null && !caja.current.contains(e.target as Node)) {
                setAbierto(false);
                setSub(null);
            }
        };
        const tecla = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setAbierto(false);
                setSub(null);
                boton.current?.focus();
            }
        };
        document.addEventListener('mousedown', fuera);
        document.addEventListener('keydown', tecla);
        return () => {
            document.removeEventListener('mousedown', fuera);
            document.removeEventListener('keydown', tecla);
        };
    }, [abierto]);

    const visibles = opciones.filter((o) => o.onClick !== undefined || (o.submenu?.length ?? 0) > 0);
    if (visibles.length === 0) {
        return null;
    }
    const lista = sub?.submenu ?? visibles;
    const ordenadas = [...lista.filter((o) => o.peligro !== true), ...lista.filter((o) => o.peligro === true)];

    return (
        <div className="rv-menu" ref={caja}>
            <button
                ref={boton}
                type="button"
                className={`rv-menu__boton${fantasma ? ' rv-menu__boton--fantasma' : ''}`}
                aria-label={etiqueta ?? t.masOpciones}
                aria-haspopup="menu"
                aria-expanded={abierto}
                aria-controls={abierto ? id : undefined}
                onClick={(e) => {
                    e.stopPropagation();
                    setSub(null);
                    setAbierto((a) => !a);
                }}
            >
                <Icono nombre="ellipsis" />
            </button>
            {abierto && movil && <button type="button" className="rv-menu__fondo" aria-label={t.cerrarMenu} onClick={() => { cerrar(); }} />}
            {abierto && (
                <div id={id} role="menu" aria-label={sub === null ? (etiqueta ?? t.masOpciones) : `${t.submenu}: ${sub.texto}`} className={`rv-menu__lista${movil ? ' rv-menu__lista--hoja' : ''}`}>
                    {sub !== null && (
                        <button type="button" role="menuitem" className="rv-menu__atras" onClick={(e) => { e.stopPropagation(); setSub(null); }}>
                            <Icono nombre="chevron-left" />
                            <span>{t.atras}</span>
                        </button>
                    )}
                    {ordenadas.map((o) => (
                        <button
                            key={o.clave}
                            type="button"
                            role="menuitem"
                            disabled={o.desactivada === true}
                            aria-haspopup={o.submenu !== undefined ? 'menu' : undefined}
                            className={`rv-menu__opcion${o.peligro === true ? ' rv-menu__opcion--peligro' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (o.submenu !== undefined) {
                                    setSub(o);
                                    return;
                                }
                                cerrar();
                                o.onClick?.();
                            }}
                        >
                            {o.icono !== undefined && <Icono nombre={o.icono} />}
                            <span className="rv-menu__texto">
                                <span>{o.texto}</span>
                                {o.detalle !== undefined && o.detalle !== '' && <small>{o.detalle}</small>}
                            </span>
                            {o.submenu !== undefined && <Icono nombre="chevron-right" />}
                        </button>
                    ))}
                    {movil && (
                        <button type="button" className="rv-menu__cerrar" onClick={() => { cerrar(); }}>
                            {t.cerrar}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
